import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, Users, Truck,
  Store, Calendar, AlertTriangle, Clock, Award, RefreshCw,
  ArrowUp, ArrowDown, BarChart3, FileText, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import PlatformRevenueBreakdown from './PlatformRevenueBreakdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdvancedAnalytics = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showPlatformDetail, setShowPlatformDetail] = useState(false);
  const [monthValue, setMonthValue] = useState('');

  const monthOptions = React.useMemo(() => {
    const opts = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 1; i <= 12; i++) {
      d.setMonth(d.getMonth() - 1);
      const y = d.getFullYear(), m = d.getMonth();
      const mm = String(m + 1).padStart(2, '0');
      const lastDay = new Date(y, m + 1, 0).getDate();
      const label = d.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-GB', { month: 'long', year: 'numeric' });
      opts.push({
        value: `${y}-${mm}-01|${y}-${mm}-${String(lastDay).padStart(2, '0')}`,
        label: label.charAt(0).toUpperCase() + label.slice(1),
      });
    }
    return opts;
  }, [language]);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customStart, customEnd]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customStart && customEnd) {
        params.set('start_date', customStart);
        params.set('end_date', customEnd);
      } else {
        params.set('period', period);
      }
      const response = await fetch(`${API_URL}/api/admin/analytics/full?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
        generateAlerts(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error(language === 'pt' ? 'Erro ao carregar analytics' : 'Error loading analytics');
    } finally {
      setLoading(false);
    }
  };

  const generateAlerts = (data) => {
    const newAlerts = [];
    
    // Check for cancellation rate
    if (data.orders?.cancellation_rate > 10) {
      newAlerts.push({
        type: 'warning',
        message: language === 'pt' 
          ? `Taxa de cancelamento alta: ${data.orders.cancellation_rate.toFixed(1)}%`
          : `High cancellation rate: ${data.orders.cancellation_rate.toFixed(1)}%`
      });
    }

    // Check for delivery delays
    if (data.delivery?.avg_time_minutes > 45) {
      newAlerts.push({
        type: 'warning',
        message: language === 'pt'
          ? `Tempo médio de entrega alto: ${data.delivery.avg_time_minutes} min`
          : `High average delivery time: ${data.delivery.avg_time_minutes} min`
      });
    }

    // Check for significant growth or decline
    if (data.growth?.orders_growth < -20) {
      newAlerts.push({
        type: 'danger',
        message: language === 'pt'
          ? `Queda significativa de pedidos: ${data.growth.orders_growth.toFixed(1)}%`
          : `Significant order decline: ${data.growth.orders_growth.toFixed(1)}%`
      });
    } else if (data.growth?.orders_growth > 30) {
      newAlerts.push({
        type: 'success',
        message: language === 'pt'
          ? `Aumento significativo de pedidos: +${data.growth.orders_growth.toFixed(1)}%`
          : `Significant order increase: +${data.growth.orders_growth.toFixed(1)}%`
      });
    }

    setAlerts(newAlerts);
  };

  const formatCurrency = (value) => `€${(value || 0).toFixed(2)}`;
  
  const formatGrowth = (value) => {
    const formatted = (value || 0).toFixed(1);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Export to PDF via backend
  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 500);
  };

  const exportParams = () => {
    const params = new URLSearchParams();
    if (customStart && customEnd) {
      params.set('start_date', customStart);
      params.set('end_date', customEnd);
    } else {
      params.set('period', period);
    }
    return params.toString();
  };

  const exportToPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/export/pdf?${exportParams()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      triggerDownload(blob, `wegoo_relatorio_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(language === 'pt' ? 'PDF exportado!' : 'PDF exported!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(language === 'pt' ? 'Erro ao exportar PDF' : 'PDF export error');
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/export/excel?${exportParams()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      triggerDownload(blob, `wegoo_relatorio_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(language === 'pt' ? 'Excel exportado!' : 'Excel exported!');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(language === 'pt' ? 'Erro ao exportar Excel' : 'Excel export error');
    }
  };

  if (showPlatformDetail) {
    return (
      <PlatformRevenueBreakdown
        token={token}
        language={language}
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onBack={() => setShowPlatformDetail(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#004E64]">
          {language === 'pt' ? 'Analytics Completos' : 'Full Analytics'}
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={period} onValueChange={(v) => { setCustomStart(''); setCustomEnd(''); setMonthValue(''); setPeriod(v); }}>
            <SelectTrigger className="w-[170px] rounded-xl">
              <Calendar size={16} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">{language === 'pt' ? 'Hoje' : 'Today'}</SelectItem>
              <SelectItem value="week">{language === 'pt' ? 'Últimos 7 dias' : 'Last 7 days'}</SelectItem>
              <SelectItem value="month">{language === 'pt' ? 'Este Mês (desde dia 1)' : 'This Month (from day 1)'}</SelectItem>
            </SelectContent>
          </Select>
          {/* Previous months archive — nothing is ever deleted */}
          <Select
            value={monthValue}
            onValueChange={(v) => {
              setMonthValue(v);
              const [s, e] = v.split('|');
              setCustomStart(s);
              setCustomEnd(e);
            }}
          >
            <SelectTrigger className="w-[190px] rounded-xl" data-testid="analytics-month-archive">
              <SelectValue placeholder={language === 'pt' ? 'Meses anteriores…' : 'Previous months…'} />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Custom date range */}
          <div className="flex items-center gap-2 bg-[#F4F1EA] rounded-xl px-2 py-1">
            <span className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'De' : 'From'}</span>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="bg-transparent text-sm focus:outline-none"
              data-testid="analytics-start-date"
            />
            <span className="text-xs text-[#9FA2B4]">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="bg-transparent text-sm focus:outline-none"
              data-testid="analytics-end-date"
            />
            {(customStart || customEnd) && (
              <button
                onClick={() => { setCustomStart(''); setCustomEnd(''); setMonthValue(''); }}
                className="text-xs text-[#FF6B35] hover:underline"
                data-testid="analytics-clear-dates"
              >
                ✕
              </button>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={exportToPDF} className="rounded-xl text-xs">
            <FileText size={14} className="mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={exportToExcel} className="rounded-xl text-xs">
            <FileSpreadsheet size={14} className="mr-1" />
            Excel
          </Button>
          <Button variant="outline" size="icon" onClick={fetchAnalytics} className="rounded-xl">
            <RefreshCw size={18} />
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, index) => (
            <div 
              key={index}
              className={`p-3 rounded-xl flex items-center gap-3 ${
                alert.type === 'danger' ? 'bg-red-50 text-red-700' :
                alert.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                'bg-green-50 text-green-700'
              }`}
            >
              <AlertTriangle size={18} />
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Main KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Pedidos' : 'Orders'}</p>
                <p className="text-2xl font-bold text-[#004E64]">{analytics?.orders?.total || 0}</p>
                <div className={`flex items-center text-xs mt-1 ${analytics?.growth?.orders_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics?.growth?.orders_growth >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                  {formatGrowth(analytics?.growth?.orders_growth)}
                </div>
              </div>
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                <Package className="text-[#FF6B35]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Receita Total' : 'Total Revenue'}</p>
                <p className="text-2xl font-bold text-[#004E64]">{formatCurrency(analytics?.revenue?.total)}</p>
                <div className={`flex items-center text-xs mt-1 ${analytics?.growth?.revenue_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics?.growth?.revenue_growth >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                  {formatGrowth(analytics?.growth?.revenue_growth)}
                </div>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="text-green-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="rounded-2xl border-0 shadow-sm bg-[#FF6B35]/5 cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-md"
          onClick={() => setShowPlatformDetail(true)}
          data-testid="platform-revenue-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Receita Plataforma' : 'Platform Revenue'}</p>
                <p className="text-2xl font-bold text-[#FF6B35]">{formatCurrency(analytics?.revenue?.platform)}</p>
                <p className="text-xs text-[#9FA2B4] mt-1">{language === 'pt' ? 'Entregas + comissões + serviços · ver detalhe' : 'Delivery + commissions + services · view detail'}</p>
              </div>
              <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-full flex items-center justify-center">
                <TrendingUp className="text-[#FF6B35]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Receita Restaurantes' : 'Restaurant Revenue'}</p>
                <p className="text-2xl font-bold text-[#2EC4B6]">{formatCurrency(analytics?.revenue?.restaurants)}</p>
                <p className="text-xs text-[#9FA2B4] mt-1">{language === 'pt' ? 'Valor da comida' : 'Food value'}</p>
              </div>
              <div className="w-12 h-12 bg-[#2EC4B6]/10 rounded-full flex items-center justify-center">
                <Store className="text-[#2EC4B6]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics Tabs */}
      <Tabs defaultValue="restaurants" className="w-full">
        <TabsList className="bg-white rounded-full p-1 shadow-sm mb-4">
          <TabsTrigger value="restaurants" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white">
            <Store size={16} className="mr-2" />
            {language === 'pt' ? 'Restaurantes' : 'Restaurants'}
          </TabsTrigger>
          <TabsTrigger value="customers" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white">
            <Users size={16} className="mr-2" />
            {language === 'pt' ? 'Clientes' : 'Customers'}
          </TabsTrigger>
          <TabsTrigger value="delivery" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white">
            <Truck size={16} className="mr-2" />
            {language === 'pt' ? 'Entregas' : 'Delivery'}
          </TabsTrigger>
        </TabsList>

        {/* Restaurants Tab */}
        <TabsContent value="restaurants">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="text-[#FF6B35]" size={20} />
                {language === 'pt' ? 'Top Restaurantes' : 'Top Restaurants'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#004E64]/10">
                      <th className="text-left py-3 px-2 text-sm text-[#9FA2B4]">#</th>
                      <th className="text-left py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Restaurante' : 'Restaurant'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Pedidos' : 'Orders'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Bruto' : 'Gross'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#FF6B35]">{language === 'pt' ? 'Comissão (15%)' : 'Commission (15%)'}</th>
                      <th className="text-right py-3 px-2 text-sm text-green-700">{language === 'pt' ? 'Líquido a pagar' : 'Net to pay'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Média' : 'Avg'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics?.restaurants?.top?.map((restaurant, index) => (
                      <tr key={restaurant.restaurant_id} className="border-b border-[#004E64]/5 hover:bg-[#004E64]/[0.02]" data-testid={`analytics-rest-${restaurant.restaurant_id}`}>
                        <td className="py-3 px-2">
                          <Badge className={index < 3 ? 'bg-[#FF6B35]' : 'bg-gray-200 text-gray-600'}>
                            {index + 1}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 font-medium text-[#004E64]">{restaurant.name}</td>
                        <td className="py-3 px-2 text-right">{restaurant.orders}</td>
                        <td className="py-3 px-2 text-right font-bold text-[#2EC4B6]">{formatCurrency(restaurant.revenue)}</td>
                        <td className="py-3 px-2 text-right font-bold text-[#FF6B35]">+{formatCurrency(restaurant.commission_owed)}</td>
                        <td className="py-3 px-2 text-right font-bold text-green-700">{formatCurrency(restaurant.net_to_pay ?? (restaurant.revenue - restaurant.commission_owed))}</td>
                        <td className="py-3 px-2 text-right text-[#9FA2B4]">{formatCurrency(restaurant.avg_order)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Total Clientes' : 'Total Customers'}</p>
                <p className="text-3xl font-bold text-[#004E64]">{analytics?.customers?.total || 0}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Novos Esta Semana' : 'New This Week'}</p>
                <p className="text-3xl font-bold text-[#2EC4B6]">{analytics?.customers?.new_this_week || 0}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Ativos' : 'Active'}</p>
                <p className="text-3xl font-bold text-[#FF6B35]">{analytics?.customers?.active || 0}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-base">{language === 'pt' ? 'Top Clientes' : 'Top Customers'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#004E64]/10">
                      <th className="text-left py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Cliente' : 'Customer'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Pedidos' : 'Orders'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Total Gasto' : 'Total Spent'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics?.customers?.top?.map((customer) => (
                      <tr key={customer.user_id} className="border-b border-[#004E64]/5">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium text-[#004E64]">{customer.name}</p>
                            <p className="text-xs text-[#9FA2B4]">{customer.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right">{customer.orders}</td>
                        <td className="py-3 px-2 text-right font-bold text-[#FF6B35]">{formatCurrency(customer.total_spent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delivery Tab */}
        <TabsContent value="delivery">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Tempo Médio' : 'Avg Time'}</p>
                <p className="text-3xl font-bold text-[#004E64]">{analytics?.delivery?.avg_time_minutes || 0} min</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Entregas Atrasadas' : 'Late Deliveries'}</p>
                <p className="text-3xl font-bold text-red-500">{analytics?.delivery?.late_deliveries || 0}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Entregadores Ativos' : 'Active Drivers'}</p>
                <p className="text-3xl font-bold text-[#2EC4B6]">{analytics?.delivery?.active_drivers || 0}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Total Entregadores' : 'Total Drivers'}</p>
                <p className="text-3xl font-bold text-[#004E64]">{analytics?.delivery?.total_drivers || 0}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-base">{language === 'pt' ? 'Top Entregadores' : 'Top Drivers'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#004E64]/10">
                      <th className="text-left py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Entregador' : 'Driver'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Entregas' : 'Deliveries'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Tempo Médio' : 'Avg Time'}</th>
                      <th className="text-right py-3 px-2 text-sm text-[#9FA2B4]">{language === 'pt' ? 'Avaliação' : 'Rating'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics?.delivery?.top_drivers?.map((driver) => (
                      <tr key={driver.driver_id} className="border-b border-[#004E64]/5">
                        <td className="py-3 px-2 font-medium text-[#004E64]">{driver.name}</td>
                        <td className="py-3 px-2 text-right">{driver.deliveries}</td>
                        <td className="py-3 px-2 text-right text-[#9FA2B4]">{driver.avg_time} min</td>
                        <td className="py-3 px-2 text-right">
                          <Badge className="bg-yellow-100 text-yellow-700">⭐ {driver.rating}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedAnalytics;
